"use strict";
/**
 * Request locale of an AppBase page or data read, shared by the server and
 * the StreamKit.
 *
 * Algorithm (RFC 4647 best-match style, with strict allow-listing):
 *   1. `?lang=xx` query parameter — case-insensitive exact match against
 *      `availableLocales`.
 *   2. `Accept-Language` header — best-match via prefix collapse
 *      (`de-DE-x-foo` -> `de-DE` -> `de`), case-insensitive against
 *      `availableLocales`. Quality factors (`q=`) are honoured: locales
 *      with `q=0` are skipped, otherwise descending q wins.
 *   3. fallback to `defaultLocale`.
 *
 * Why URL strategy over Vary: Vary: Accept-Language fragments the CDN
 * cache (Bunny tracks per-header-value, hit-rate falls off a cliff).
 * `?lang=` is part of the cache-key by default, so each locale gets a
 * single warm entry.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveRequestLocale = resolveRequestLocale;
function resolveRequestLocale(input) {
    const available = new Map(input.availableLocales.map(l => [l.toLowerCase(), l]));
    const queryCandidate = pickFirst(input.queryLang);
    if (queryCandidate) {
        const match = available.get(queryCandidate.toLowerCase());
        if (match)
            return { locale: match, source: 'query' };
    }
    if (input.acceptLanguage) {
        const ranked = parseAcceptLanguage(input.acceptLanguage);
        for (const tag of ranked) {
            const match = matchAvailable(tag, available);
            if (match)
                return { locale: match, source: 'accept-language' };
        }
    }
    return { locale: input.defaultLocale, source: 'default' };
}
function pickFirst(value) {
    if (Array.isArray(value))
        return value[0];
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}
/**
 * Walk the Accept-Language header into a q-sorted list of tags.
 * Skips entries with `q=0` (explicit reject). Stable sort by descending q.
 */
function parseAcceptLanguage(header) {
    const entries = header
        .split(',')
        .map(part => {
        const [tag, ...params] = part
            .trim()
            .split(';')
            .map(s => s.trim());
        if (!tag)
            return null;
        let q = 1;
        for (const p of params) {
            const m = p.match(/^q=([0-9.]+)$/);
            if (m) {
                const parsed = Number.parseFloat(m[1]);
                if (Number.isFinite(parsed))
                    q = parsed;
            }
        }
        if (q <= 0)
            return null;
        return { tag, q };
    })
        .filter((x) => x !== null)
        .sort((a, b) => b.q - a.q);
    return entries.map(e => e.tag);
}
/**
 * Match a single Accept-Language tag against the availableLocales map.
 * Tries exact match first, then walks the BCP-47 ladder (`de-DE-x-foo` →
 * `de-DE` → `de`).
 */
function matchAvailable(tag, available) {
    const segments = tag.split('-');
    for (let n = segments.length; n > 0; n--) {
        const candidate = segments.slice(0, n).join('-').toLowerCase();
        const match = available.get(candidate);
        if (match)
            return match;
    }
    return undefined;
}
